import axios from 'axios';
import type { ComputedRef, InjectionKey } from 'vue';
import { http } from './http';
import {
  PRODOTTIANAGRAFICA_MONITORING,
  PRODUCTCATEGORY,
  REQUIREMENTTYPE,
  OPPORTUNITA_STATO,
  PRODOTTIANAGRAFICA_SUPPORT,
  PRODOTTIORDINI_PROVISIONDB,
  PRODOTTIORDINI_PROVISIONPHP,
  REGION,
  PRODOTTIORDINI_MOTIVAZIONEDISDETTA,
  PRODOTTIANAGRAFICA_DURATA,
} from './common/consts';

export type ResourceId = number | string;
function isResourceId(id: any): id is ResourceId {
  return typeof id === 'string' || typeof id === 'number';
}

export type GenericResource = Record<string, any>;

export type ResourceData<T> = FormData | (T & { id?: ResourceId });
function isResourceData<T>(data: any): data is ResourceData<T> {
  return data instanceof FormData || typeof data === 'object';
}

export type QueryParams = Record<string, string | number>;

export class Resource<
  GetT = GenericResource,
  GetListT = GetT[],
  PutT = Partial<GetT>,
  PostT = Partial<GetT>,
  RespPutT = unknown,
  RespPostT = unknown,
  RespDelT = unknown,
> {
  model: string;
  basepath: string;
  queryParams: QueryParams;

  constructor(model: string, queryParams: QueryParams = {}, basepath = 'rest') {
    this.model = model;
    this.queryParams = queryParams;
    this.basepath = basepath;
  }

  async get(): Promise<GetListT>;
  async get(id: ResourceId, default404?: GetT): Promise<GetT>;
  async get(id?: ResourceId, default404?: GetT): Promise<any> {
    try {
      return (await http.get(this.url(id))).data;
    } catch (e: any) {
      if (default404 !== null && typeof default404 !== 'undefined' && axios.isAxiosError(e) && e.response?.status === 404) {
        return default404;
      }
      throw e;
    }
  }

  processResourceDataFiles(data: ResourceData<PostT | PutT>) {
    if (!isResourceData(data)) {
      return data;
    }
    if (!Object.values(data).some(v => v instanceof Blob || v instanceof FileList)) {
      return data;
    }

    const formdata = new FormData();
    const jsonData: GenericResource = {};

    for (const [k, v] of Object.entries(data)) {
      if (v instanceof Blob) {
        formdata.append(k, v);
      } else if (v instanceof FileList) {
        for (const f of v) {
          formdata.append(`${k}[]`, f);
        }
      } else {
        jsonData[k] = v;
      }
    }

    formdata.append('json', JSON.stringify(jsonData));
    return formdata;
  }

  async post(data: ResourceData<PostT>) {
    return (await http.post<RespPostT>(this.url(), this.processResourceDataFiles(data))).data;
  }

  async put(id: ResourceId, data: ResourceData<PutT>) {
    return (await http.put<RespPutT>(this.url(id), this.processResourceDataFiles(data))).data;
  }

  async delete(id: ResourceId) {
    return (await http.delete<RespDelT>(this.url(id))).data;
  }

  save(data: ResourceData<PostT>): Promise<RespPostT>;
  save(id: null, data: ResourceData<PostT>): Promise<RespPostT>;
  save(id: ResourceId, data: ResourceData<PutT>): Promise<RespPutT>;
  save(id?: ResourceId | ResourceData<PostT> | null, data: ResourceData<PostT | PutT> | null = null) {
    if (isResourceData(id)) {
      data = id;
      id = null;
    }

    if (data === null) {
      throw new Error('missing data');
    }

    if (typeof id === 'undefined' || id === null) {
      if (data instanceof FormData) {
        const formId = data.get('id');
        if (!(formId instanceof File)) {
          id = formId;
        }
      } else {
        id = data.id;
      }
    }

    if (isResourceId(id)) {
      return this.put(id, data as ResourceData<PutT>);
    }
    return this.post(data as ResourceData<PostT>);
  }

  url(id?: ResourceId) {
    if (Array.isArray(id)) {
      id = id.join('-');
    }
    id = typeof id === 'undefined' || id === null ? '' : `/${id}`;
    return `/${this.basepath}/${this.model}${id}${this.querystring}`;
  }

  child(id: ResourceId, model: string) {
    let url = this.url(id);
    url = url.substring(1, Math.max(url.length - this.querystring.length, 0) + 1);
    return new Resource(model, this.queryParams, url);
  }

  get querystring() {
    const q = this.queryParams;
    const keys = Object.keys(q).filter(k => typeof q[k] != 'undefined');
    if (!keys.length) {
      return '';
    }
    return `?${keys.map(k => `${k}=${encodeURIComponent(q[k] ?? '')}`).join('&')}`;
  }
}

export type Currency = 'EUR' | 'CHF';

export type MessageResponse = {
  message: string;
  level: string;
};

export type PHPDateTime = {
  date: string;
  timezone: string;
  timezone_type: number;
};

export type ProductOrder = {
  id: number;
  id_ordine: number;
  dominio: string | null;
  qt: number;
  prezzo: string;
  sconto_minimo: string;
  free_period: number;
  stato_tecnico: string;
  data_fine: string;
  nome: string;
  rinnovo_automatico: boolean;
  disdetto: boolean;
  durata: (typeof PRODOTTIANAGRAFICA_DURATA)[number];
  nfr: string;
  n_upgrades: number;
  note_cliente: string | null;
  last_bw_upgrade: string;
  free_bw_upgrade: string;
  bw_upgrade_price: number;
  registrazione_dominio: boolean;
  codice: string;
  transfer_site_required: boolean;
  transfer_mail_required: boolean;
  ask_site_transfer: boolean;
  ask_mail_transfer: boolean;
  days_to_expiration: number;
  grace_period: number;
};

export type ProductOrderDetail = {
  id: number;
  id_ordine: number;
  dominio: string | null;
  qt: number;
  prezzo: number;
  importo: number;
  valore_storno: number | null;
  upgradable_products: {
    id: number;
    codice: string;
    nome: string;
    durata: (typeof PRODOTTIANAGRAFICA_DURATA)[number];
    prezzo: number;
    importo: number;
    cicli: number;
  }[];
  sconto_minimo: number;
  free_period: number;
  stato_tecnico: string;
  stato_amministrativo: string;
  data_fine: string;
  codice: string;
  nome: string;
  rinnovo_automatico: boolean;
};

export type PublicCancellationReason = Exclude<
  (typeof PRODOTTIORDINI_MOTIVAZIONEDISDETTA)[number],
  'Nessun riscontro' | 'Altro' | 'Upgrade' | 'Test' | 'Insoluto' | 'Prezzo'
>;

export type RenewalInfo = {
  nominativo: string;
  rinnovo_automatico?: boolean;
  note_cliente?: string;
  stato_amministrativo?: 'attivo' | 'non rinnovare';
  motivazione_disdetta?: PublicCancellationReason;
  authcode_request?: boolean;
};

export class ProductOrdersResource extends Resource<ProductOrderDetail[], ProductOrder[], RenewalInfo, never, { rinnovo_automatico: boolean }> {
  constructor(queryParams: QueryParams = {}) {
    super('product_orders', queryParams);
  }
}

export type ProductsExpiringPayments = string[];

export class ProductsExpiringPaymentsResource extends Resource<never, Record<number, ProductsExpiringPayments>> {
  constructor(queryParams: QueryParams = {}) {
    super('products_expiring_payments', queryParams);
  }
}

export type OrderProduct = {
  id: number;
  codice: string;
  nome: string;
  qt: number;
  prezzo: number;
  dominio: string | null;
  stato_tecnico: string;
  registrazione_dominio: boolean;
  transfer_site_required: boolean;
  transfer_mail_required: boolean;
  ask_site_transfer: boolean;
  ask_mail_transfer: boolean;
};

export type Order = {
  id: number;
  id_azienda: number;
  data: PHPDateTime;
  amount: number;
  total: number;
  vat: number;
  method: string;
  pagamento: string;
  stato: string;
  products: OrderProduct[];
};

export class OrdersResource extends Resource<Order> {
  constructor(queryParams: QueryParams = {}) {
    super('orders', queryParams);
  }
}

export type Payment = {
  amount: number;
  currency: Currency;
  data: string;
  descrizione: string;
  fattura: string;
  id: number;
  id_fattura: number;
  pending: boolean;
  tipo: string;
};

export class PaymentsResource extends Resource<Payment> {
  constructor(queryParams: QueryParams = {}) {
    super('payments', queryParams);
  }
}

export type OfferProductOrder = {
  id: number;
  codice: string;
  nome: string;
  durata: (typeof PRODOTTIANAGRAFICA_DURATA)[number];
  descrizione: string;
  dominio: string | null;
  imponibile: number;
  importo: number;
  sconto: number;
  qt: number;
  note_fattura: string | null;
  migratedFrom?: OfferProductOrder;
};

export type OfferTotals = Record<(typeof PRODOTTIANAGRAFICA_DURATA)[number], { listino: number; scontato: number }>;

export type ProvisionInfo = {
  codice: string;
  dominio: string;
  qt?: number;
  os?: string;
  webserver?: 'Apache' | 'NGINX';
  provision_db?: (typeof PRODOTTIORDINI_PROVISIONDB)[number];
  provision_php?: (typeof PRODOTTIORDINI_PROVISIONPHP)[number];
  newService?: boolean;
  siteTransfer?: boolean;
  mailTransfer?: boolean;
  notForResale?: boolean;
  provision_region?: (typeof REGION)[number];
  provision_backup_region?: (typeof REGION)[number];
  tecnologie?: string[];
};

export type MotivazioniRifiuto = 'prezzo' | 'altro fornitore' | 'tempistiche' | 'non più necessario' | 'altro';

export type Offer = {
  id: number;
  title: string;
  amount: string;
  currency: Currency;
  data: string;
  gran_totale: number;
  gran_totale_ivato: number;
  id_azienda: number;
  id_reseller: number;
  note_offerta: string;
  prodotti: Omit<OfferProductOrder[], 'migratedFrom'>;
  prodotti_disdire: Omit<OfferProductOrder[], 'migratedFrom'>;
  ragione_sociale: string;
  sconto_custom: string;
  stato: 'ordine' | 'rifiutata' | 'in_corso';
  free_period: number;
  testing_mode: 'rinnova' | 'disdici' | null;
  titolo_offerta: string;
  totali: OfferTotals;
  totali_disdetti: OfferTotals;
  valore_storno: number;
  enable_orders: boolean;
};

export type OfferProductDetail = OfferProductOrder & { disdire: boolean };

export type OfferUpdate =
  | { stato: 'rifiutata'; motivazione_rifiuto: MotivazioniRifiuto }
  | { stato: 'confermata'; cashback_amount?: number; title?: string; note?: string };
export class OffersResource extends Resource<Offer, Offer[], OfferUpdate, never, MessageResponse> {
  constructor(queryParams: QueryParams = {}) {
    super('offers', queryParams);
  }
}

export type WizardOfferUpdate = {
  id_azienda: number;
  action: 'confirm' | 'pdf' | 'mailme' | 'mailcustomer';
  title?: string;
  note?: string;
};

export type WizardAction = 'confirm' | 'pdf' | 'mailme' | 'mailcustomer';

export class WizardOfferProductsResource extends Resource<unknown, unknown, unknown, unknown, unknown, unknown, Offer> {
  constructor(queryParams: QueryParams = {}, url: string) {
    super('wizard_offer_products', queryParams, url);
  }
}

export class WizardOfferResource extends Resource<
  unknown,
  unknown,
  WizardOfferUpdate,
  Partial<Offer>,
  { message: string; level: string },
  { message: string; level: string; offerta: Offer }
> {
  constructor(queryParams: QueryParams = {}) {
    super('wizard_offer', queryParams);
  }

  products(id: ResourceId) {
    let url = this.url(id);
    url = url.substring(1, Math.max(url.length - this.querystring.length, 0) + 1);
    return new WizardOfferProductsResource(this.queryParams, url);
  }
}

export type Contract = {
  id: number | null;
  nome: string;
  testo: string;
  titolo: string;
};

export class ContractResource extends Resource<Contract> {
  constructor(queryParams: QueryParams = {}) {
    super('contract', queryParams);
  }
}

export type Company = {
  ack_marketing: boolean;
  ack_privacy: boolean;
  ack_terms: boolean;
  cap: string;
  citta: string;
  cod_pagamento: string;
  codice_fiscale: string | null;
  contratto_cliente: boolean;
  currency: Currency;
  currency_locked: boolean;
  email_pec: string | null;
  fatturante_strid: string;
  fatturazione_mensile: boolean;
  csv_invoice: boolean;
  hasCompleteBillingData: boolean;
  id: number;
  id_pagamento: number;
  indirizzo: string;
  is_verified_billing: boolean;
  iva: string;
  lingua: string;
  locked: boolean;
  metodi_pagamento: ('axerve-cc' | 'axerve-paypal' | 'paypal')[];
  pagamento: string;
  partita_iva: string;
  provincia: string;
  ragione_sociale: string;
  sdi: string | null;
  stato: string;
  telefono: string | null;
  tipologia: 'Company' | 'Private';
  use_credit: boolean;
  id_main_skill: number | null;
  requirements: number[];
  disableRecurringPayments: boolean;
  cashback_months: number[];
  approval: 'in approvazione cliente' | 'in approvazione rivenditore' | 'approvato' | null;
};

export const CompanyInjectionKey = Symbol('Company') as InjectionKey<Company | ComputedRef<Company>>;

export class CompanyResource extends Resource<Company, never, Partial<Company>, never, Company, never> {
  constructor(queryParams: QueryParams = {}) {
    super('company', queryParams);
  }

  catalog(id: ResourceId) {
    let url = this.url(id);
    url = url.substring(1, Math.max(url.length - this.querystring.length, 0) + 1);
    return new CatalogResource(this.queryParams, url);
  }

  priceList(id: ResourceId) {
    let url = this.url(id);
    url = url.substring(1, Math.max(url.length - this.querystring.length, 0) + 1);
    return new PriceListResource(this.queryParams, url);
  }

  customOfferNotes(id: ResourceId) {
    let url = this.url(id);
    url = url.substring(1, Math.max(url.length - this.querystring.length, 0) + 1);
    return new CustomOfferNotesResource(this.queryParams, url);
  }
}

export type Cashback = {
  amount: number;
};

export class CashbackResource extends Resource<Cashback> {
  constructor(queryParams: QueryParams = {}) {
    super('cashback', queryParams);
  }
}

export type DomainRegistrant = Pick<
  Company,
  'id' | 'tipologia' | 'ragione_sociale' | 'stato' | 'cap' | 'indirizzo' | 'citta' | 'provincia' | 'partita_iva' | 'codice_fiscale'
> & {
  nome: string;
  cognome: string;
  email: string;
  new_registration: boolean | null;
  anytime: boolean | null;
  authcode: string;
  usa_dati_fatturazione: boolean;
  artera_nameservers: boolean;
  nameservers: string | null;
  default_zone: boolean | null;
  dns_zone: string | null;
};

export class DomainRegistrantResource extends Resource<DomainRegistrant> {
  constructor(queryParams: QueryParams = {}) {
    super('domain_registrant', queryParams);
  }
}

export type DomainContactEntityType = 'Individual' | 'Company' | 'Freelance' | 'NoProfit' | 'Public' | 'Other' | 'Foreign';

export type DomainContact = {
  id: number;
  entity_type: DomainContactEntityType;
  name: string;
  email: string;
  company: string | null;
  country: string;
  zipcode: string;
  address: string;
  city: string;
  province: string;
  reg_code: string | null;
  consent_for_publishing: boolean;
  created: string;
};

export type DomainContactCreate = Omit<DomainContact, 'id' | 'created' | 'entity_type'> & {
  entity_type: Exclude<DomainContactEntityType, 'Foreign'>;
};

export class DomainContactResource extends Resource<DomainContact, DomainContact[], never, DomainContactCreate, never, { id: number }> {
  constructor(queryParams: QueryParams = {}) {
    super('domain_contacts', queryParams);
  }
}

export type HostingTransferAccessType = 'ftp' | 'ssh' | 'panel' | 'webaccess';

export type HostingTransfer = {
  id: number;
  access_type: HostingTransferAccessType | null;
  url: string;
  user: string;
  password: string;
  dbName: string;
  dbUser: string;
  dbPassword: string;
  pmaUrl: string;
  cms: string;
  maintenanceSite: boolean;
  subdomains: string;
  imap: string;
  mails: [
    {
      name: string;
      password: string;
    },
  ];
  note: string;
};

export class HostingTransferResource extends Resource<Partial<HostingTransfer> & { id: number }> {
  constructor(queryParams: QueryParams = {}) {
    super('hosting_transfer', queryParams);
  }
}

export type MailTransfer = {
  id: number;
  imap: string;
  mails: {
    name: string;
    password: string;
  }[];
};

export class MailTransferResource extends Resource<MailTransfer> {
  constructor(queryParams: QueryParams = {}) {
    super('mail_transfer', queryParams);
  }
}

export type Opportunity = {
  id: number;
  cliente?: string;
  data: string;
  nome: string;
  descrizione?: string;
  dominio?: string;
  tempistiche?: string;
  budget?: string;
  contatti?: string;
  skills: string[];
  tech_skills: string[];
  business_sectors: string[];
  stato: 'accettata' | 'rifiutata' | 'scaduta' | 'attesa';
};

export class OpportunityResource extends Resource<Opportunity> {
  constructor(queryParams: QueryParams = {}) {
    super('opportunities', queryParams);
  }
}

export type Project = Omit<Opportunity, 'cliente' | 'stato'> & {
  stato: (typeof OPPORTUNITA_STATO)[number];
  partner?: string | null;
};
export type ProjectCreate = Omit<Project, 'id' | 'cliente' | 'data' | 'requisiti' | 'stato' | 'partner'> & { id_requirements: number[] };

export class ProjectResource extends Resource<Project, Project[], never, ProjectCreate> {
  constructor(queryParams: QueryParams = {}) {
    super('projects', queryParams);
  }
}

export type TicketContact = {
  email: string;
  name?: string;
};

export type TicketInfo = {
  id: number;
  data: string;
  titolo: string;
  urgente: boolean;
  stato: 'aperto' | 'chiuso';
  nr_messaggi: number;
  prodotto: string | null;
  department: 'tecnica' | 'amministrativa' | 'commerciale' | null;
};

export type Ticket = TicketInfo & {
  to: TicketContact[];
  cc: TicketContact[];
};

export type TicketList = (TicketInfo & { access: 'owner' | 'follower' | null })[];

export type TicketCreate = {
  titolo: string;
  messaggio?: string;
  urgente?: boolean;
  dettaglio?: string;
  cc?: TicketContact[];
};

export class TicketResource extends Resource<Ticket, TicketList, Partial<Ticket>, TicketCreate> {
  constructor(queryParams: QueryParams = {}) {
    super('tickets', queryParams);
  }

  messages(id: ResourceId) {
    let url = this.url(id);
    url = url.substring(1, Math.max(url.length - this.querystring.length, 0) + 1);
    return new TicketMessageResource(this.queryParams, url);
  }

  followers(id: ResourceId) {
    let url = this.url(id);
    url = url.substring(1, Math.max(url.length - this.querystring.length, 0) + 1);
    return new TicketFollowersResource(this.queryParams, url);
  }
}

export type TicketMessage = {
  id: number;
  messaggio: string;
  data: string;
  id_offerta_allegata: number | null;
  id_fattura_allegata: number | null;
  utente: string | null;
  operatore: string | null;
  nr_fattura: string | null;
  serie_fattura: string | null;
  anno_fattura: string | null;
  allegati: any[];
};

export class TicketMessageResource extends Resource<TicketMessage> {
  constructor(queryParams: QueryParams = {}, url: string) {
    super('messages', queryParams, url);
  }
}

export class TicketFollowersResource extends Resource {
  constructor(queryParams: QueryParams = {}, url: string) {
    super('followers', queryParams, url);
  }
}

export class UrgentTicketCostResource extends Resource<number> {
  constructor(queryParams: QueryParams = {}) {
    super('ticket_cost', queryParams);
  }
}

export class AreWeClosedResource extends Resource<number> {
  constructor(queryParams: QueryParams = {}) {
    super('are_we_closed', queryParams);
  }
}

export class CpanelLoginResource extends Resource<string> {
  constructor(queryParams: QueryParams = {}) {
    super('cpanel_login', queryParams);
  }
}

export class WhmLoginResource extends Resource<string> {
  constructor(queryParams: QueryParams = {}) {
    super('whm_login', queryParams);
  }
}

export class SSHKeysResource extends Resource<never, string[], string, string> {
  constructor(queryParams: QueryParams = {}) {
    super('ssh_keys', queryParams);
  }
}

export type Server = {
  id: number;
  nome: string;
  os: string | null;
  osrelease: string | null;
  fqdn: string | null;
  ip4_public: string | null;
  is_zenith: boolean;
  allow_user_power_cycle: boolean;
  is_under_maintenance: boolean;
  has_console: boolean;
  has_cockpit: boolean;
  cpanel: string | null;
  server_location: string | null;
  backup_location: string | null;
  feature: {
    core: number;
    ram: number;
    spazio: number;
  } | null;
  stack: string;
};

export class ServerResource extends Resource<Server> {
  constructor(queryParams: QueryParams = {}) {
    super('servers', queryParams);
  }
}

export type ServerPowerAction = 'start' | 'stop' | 'restart';

export type ServerPowerState = { state: 'on' | 'off' };

export class ServerPowerStateResource extends Resource<ServerPowerState, never, { action: ServerPowerAction }, never, MessageResponse> {
  constructor(queryParams: QueryParams = {}) {
    super('server_power_state', queryParams);
  }
}

export type ProductFeatures = {
  gpu: string | null;
  gpu_ram: string | null;
  core: string | null;
  ram: string | null;
  spazio: number | null;
  traffico: number | null;
  sistema_operativo: string | null;
  pannello: string | null;
  servizio_email: boolean;
  registrazione_dominio: boolean;
  database: boolean;
  gestione_dns: boolean;
  backup: string | null;
  support: (typeof PRODOTTIANAGRAFICA_SUPPORT)[number] | null;
  monitoring: (typeof PRODOTTIANAGRAFICA_MONITORING)[number] | null;
  utenti: number | null;
  nr_domini: number | null;
  ip_dedicato: string | null;
  redirect: boolean;
  ssl_certificate: boolean;
  ssh_access: boolean;
  imunify360: boolean;
  ddos_protection: boolean;
  waf: boolean;
  litespeed: boolean;
  ttfb: number | null;
  io: number | null;
  iops: number | null;
  ep: number | null;
  uptime: number | null;
  inode: boolean;
  descrizione: string | null;
};

export type CatalogProduct = ProductFeatures & {
  id: number;
  categoria: (typeof PRODUCTCATEGORY)[number];
  codice: string;
  durata: (typeof PRODOTTIANAGRAFICA_DURATA)[number];
  group_code: string | null;
  nome: string;
  prezzo_chf: number;
  prezzo_eur: number;
  prezzo_ivato_chf: number;
  prezzo_ivato_eur: number;
  prezzo_scontato_chf: number;
  prezzo_scontato_eur: number;
  prezzo_clienti_eur?: number | null;
  prezzo_clienti_chf?: number | null;
  richiesta_quantita: boolean;
  richiesto_dominio: boolean;
  tld: string | null;
  gestione: string | null;
};

export class CatalogResource extends Resource<CatalogProduct> {
  constructor(queryParams: QueryParams = {}, url: string) {
    super('catalog', queryParams, url);
  }
}

export type PriceList = {
  id: number;
  prezzo_chf: number;
  prezzo_eur: number;
  prezzo_ivato_chf: number;
  prezzo_ivato_eur: number;
  prezzo_scontato_chf: number;
  prezzo_scontato_eur: number;
  prezzo_clienti_eur?: number | null;
  prezzo_clienti_chf?: number | null;
};

export class PriceListResource extends Resource<PriceList> {
  constructor(queryParams: QueryParams = {}, url: string) {
    super('price_list', queryParams, url);
  }
}

export type CategoryCustomPrice = {
  category: string;
  markup: number;
};

export class CategoryCustomPricesResource extends Resource<never, CategoryCustomPrice[], never, CategoryCustomPrice[]> {
  constructor(queryParams: QueryParams = {}) {
    super('category_custom_prices', queryParams);
  }
}

export type CustomOfferNotes = {
  id: number;
  title: string;
  text: string;
};

export class CustomOfferNotesResource extends Resource<CustomOfferNotes> {
  constructor(queryParams: QueryParams = {}, url: string) {
    super('custom_offer_notes', queryParams, url);
  }
}

export type AccountMovementType = 'provvigioni' | 'segnalazioni' | 'comarketing' | 'cashback';

export type AccountMovement = {
  id: number;
  amount: number;
  dettagli: string;
  tipo: AccountMovementType;
  data: string;
};

export type AccountMovements = {
  movements: AccountMovement[];
  collect_from: number;
  referral_percentage: number;
  comarketing_percentage: number;
  cashback_percentage: number;
  cashback_until: string | null;
  cashback_months: number[];
  iban: string | null;
};

export class AccountMovementsResource extends Resource<never, AccountMovements> {
  constructor(queryParams: QueryParams = {}) {
    super('account_movements', queryParams);
  }
}

export type DnsRecord = {
  domain: string;
  ttl: number | null;
  type: string;
  weight: number | null;
  target: string;
};

export class DnsResource extends Resource<DnsRecord[], never, Partial<DnsRecord>[], Partial<DnsRecord>[]> {
  constructor(queryParams: QueryParams = {}) {
    super('dns', queryParams);
  }
}

export type CompanyAccess = {
  azienda: string;
  grants: string[];
};

export class CompanyAccessResource extends Resource<never, Record<number, CompanyAccess>> {
  constructor(queryParams: QueryParams = {}) {
    super('company_access', queryParams);
  }
}

export type MyCustomersAccess = {
  id: number;
  ragione_sociale: string;
  indirizzo: string | null;
  provincia: string | null;
  cap: string | null;
  citta: string | null;
  stato: string;
  country_code: string;
  partita_iva: string | null;
  currency: Currency;
  currency_locked: boolean;
  vat: number;
  presented: false;
  enable_orders: boolean;
  contact_name: string | null;
  contact_mail: string | null;
  is_reseller: boolean;
  custom_offer_notes: CustomOfferNotes[];
  has_complete_billing_data: boolean;
};

/** Azienda presentata dal reseller: dei dati è disponibile solo la ragione sociale */
export type MyPresentedCustomer = {
  id: number;
  ragione_sociale: string;
  presented: true;
};

/** Solo i clienti diretti del reseller */
export class MyCustomersResource extends Resource<never, MyCustomersAccess[]> {
  constructor(queryParams: QueryParams = {}) {
    super('my_customers', queryParams);
  }
}

/** Clienti diretti del reseller e aziende da lui presentate */
export class MyCustomersWithPresentedResource extends Resource<never, (MyCustomersAccess | MyPresentedCustomer)[]> {
  constructor() {
    super('my_customers', { show_presented: 1 });
  }
}

export type Profile = {
  id: number;
  title: string | null;
  firstname: string | null;
  lastname: string | null;
  street: string | null;
  postalcode: string | null;
  city: string | null;
  province: string | null;
  country: string | null;
  phone: string | null;
  mobile: string | null;
  mail: string;
  newsletter_subscription: boolean;
  email_subscriptions: string[];
  grants: string[];
  twofa: boolean;
};

export class ProfilesResource extends Resource<Profile> {
  constructor(queryParams: QueryParams = {}) {
    super('profiles', queryParams);
  }
}
export type CreditDocument = {
  tipo: 'provvigioni' | 'comarketing';
  importo: number;
  messaggio: string;
  iban: string | null;
  allegati: FileList;
};

export class CreditDocumentsResource extends Resource<CreditDocument> {
  constructor(queryParams: QueryParams = {}) {
    super('credit_documents', queryParams);
  }
}

export type CompanyLogo = string;

export class CompanyLogoResource extends Resource<CompanyLogo, never, never, never> {
  constructor(queryParams: QueryParams = {}) {
    super('company_logo', queryParams);
  }
}

export type CompanyBrochure = string;

export class CompanyBrochureResource extends Resource<CompanyBrochure, never, never, never> {
  constructor(queryParams: QueryParams = {}) {
    super('company_brochure', queryParams);
  }
}

export type Introduction = {
  presentazione: string | null;
  has_logo: boolean;
  logo: File | null;
  has_brochure: boolean;
  brochure: File | null;
  categoria_prospect: string | null;
  dipendenti: string | null;
  anni_attivita: string | null;
  fatturato: string | null;
  clienti: string | null;
  dimensione_clienti: string[];
  associazioni: string[];
  preferenza_fatturazione: string | null;
  admin_pros: string | null;
  admin_cons: string | null;
  admin_feature_request: string | null;
  receive_opportunity: boolean;
};

export type IntroductionCreate = Omit<Introduction, 'logo' | 'brochure'>;

export class IntroductionResource extends Resource<Introduction, Introduction[], IntroductionCreate, IntroductionCreate> {
  constructor(queryParams: QueryParams = {}) {
    super('introduction', queryParams);
  }
}

export type ServicePannello = {
  id: number;
  sku: string;
  traffico: number | null;
  auto_upgrade_bw: boolean;
  username: string;
  server_fqdn: string | null;
  server_location: string | null;
  backup_location: string | null;
  bwused: number | null;
  bwlimit: number | null;
  id_prodotto: number;
  quotaused: number | null;
  quotalimit: number | null;
  tipo: string;
  tipo_pannello: null | 'WHM' | 'cPanel' | 'cPanel - mail only' | 'cPanel - dns only' | 'Cockpit' | 'Plesk' | 'vCenter / vSphere';
  has_whm_key: boolean;
};

export type Service = {
  dominio: string;
  pannello?: ServicePannello;
  posta?: ServicePannello;
  dns?: boolean;
  id_registrazione_dominio?: number;
  redirect?: { id: number; destinazione: string };
  id_redirect?: number;
  pecs?: string[];
};

export class ServiceResource extends Resource<Service, Service[]> {
  constructor(queryParams: QueryParams = {}) {
    super('services', queryParams);
  }
}

export type Requirements = {
  id: number;
  nome: string;
  tipo: (typeof REQUIREMENTTYPE)[number];
};

export class RequirementsResource extends Resource<never, Requirements[]> {
  constructor(queryParams: QueryParams = {}) {
    super('requirements', queryParams);
  }
}

export type WelcomeEmail = {
  subject: string;
  body: string;
};

export class WelcomeEmailResource extends Resource<WelcomeEmail, unknown, unknown, { idAzienda: number; email: string }> {
  constructor(queryParams: QueryParams = {}) {
    super('welcome_email', queryParams);
  }
}

export type HostingSettings = {
  primary_panel_email: string | null;
  secondary_panel_email: string | null;
};

export class HostingSettingsResource extends Resource<HostingSettings, never, HostingSettings> {
  constructor(queryParams: QueryParams = {}) {
    super('hosting_settings', queryParams);
  }
}

export type Transfer = {
  id: number;
  dominio: string;
  type: 'domain' | 'site' | 'mail' | 'site-mail';
  status: 'pending data' | 'to schedule' | 'scheduled' | 'in progress' | 'customer testing' | 'completed' | 'on hold' | 'canceled';
  date: string;
  emails: string[];
  codice_prodotto: string;
  nome_prodotto: string;
};

export type Transfers = { dominio: string; transfers: Transfer[] }[];

export class TransferResource extends Resource<Transfer, Transfers> {
  constructor(queryParams: QueryParams = {}) {
    super('transfers', queryParams);
  }
}

export type ChangeBillingRequest = {
  ids: number[];
  customer_id: number;
};

export class ChangeBillingRequestResource extends Resource<never, never, never, ChangeBillingRequest> {
  constructor(queryParams: QueryParams = {}) {
    super('change_billing_request', queryParams);
  }
}
